import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, Check, Minus, Plus } from '@phosphor-icons/react';
import { Link, useParams } from 'react-router-dom';
import { apiService } from '../../config/api';
import { useSettings } from '../../context/SettingsContext';
import { useAuth } from '../../context/AuthContext';
import { getText } from '../../utils/language';
import { useI18n } from '../../i18n';
import LoginModal from '../auth/LoginModal';
import RegisterModal from '../auth/RegisterModal';

const ProductDetail = ({ mode = 'store' }) => {
  const { id } = useParams();
  const { isAuthenticated } = useAuth();
  const { settings } = useSettings();
  const { language, isRTL, t } = useI18n();
  const [artwork, setArtwork] = useState(null);
  const [relatedArtworks, setRelatedArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [frameOption, setFrameOption] = useState('No Frame');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [pendingCartItem, setPendingCartItem] = useState(null);
  const isGallery = mode === 'gallery';

  const formatCurrency = (amount = 0) => {
    const currency = settings?.payment?.currency || artwork?.currency || 'USD';
    try {
      return new Intl.NumberFormat(language === 'pt' ? 'pt-PT' : language === 'ar' ? 'ar' : 'en', { style: 'currency', currency }).format(amount);
    } catch {
      return `${currency} ${Number(amount).toFixed(2)}`;
    }
  };

  const fetchArtworkData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/artworks/${id}`, { surface: isGallery ? 'gallery' : 'store' });
      if (!response || Array.isArray(response) || !response._id) {
        setArtwork(null);
        setRelatedArtworks([]);
        return;
      }
      setArtwork(response);
      setSelectedImage(0);
      const related = await apiService.get('/artworks', { category: response.category, limit: 5, surface: isGallery ? 'gallery' : 'store' });
      setRelatedArtworks((related?.artworks || []).filter((item) => item._id !== id).slice(0, 4));
    } catch (error) {
      console.error('Error fetching artwork:', error);
      setArtwork(null);
    } finally {
      setLoading(false);
    }
  }, [id, isGallery]);

  useEffect(() => { fetchArtworkData(); }, [fetchArtworkData]);

  const images = useMemo(() => Array.isArray(artwork?.images) ? artwork.images.filter(Boolean) : [], [artwork]);
  const displayTitle = getText(artwork?.title, language, 'Untitled');
  const displayDescription = getText(artwork?.description, language, '');
  const arabicTitle = artwork?.title && typeof artwork.title === 'object' ? artwork.title.ar : '';
  const arabicDescription = artwork?.description && typeof artwork.description === 'object' ? artwork.description.ar : '';
  const frames = artwork?.frameOptions?.length ? artwork.frameOptions : [t('detail.noFrame')];
  const availableQuantity = Number(artwork?.availableQuantity || 0);
  const inStock = availableQuantity > 0;

  const persistCartItem = useCallback((item) => {
    const cartItems = JSON.parse(localStorage.getItem('cartItems') || '[]');
    const existingIndex = cartItems.findIndex((cartItem) => cartItem.id === item.id && cartItem.frameOption === item.frameOption);
    if (existingIndex >= 0) {
      cartItems[existingIndex].quantity += item.quantity;
      cartItems[existingIndex].totalPrice = cartItems[existingIndex].price * cartItems[existingIndex].quantity;
    } else {
      cartItems.push(item);
    }
    localStorage.setItem('cartItems', JSON.stringify(cartItems));
    window.dispatchEvent(new Event('cartUpdated'));
  }, []);

  const handleAddToCart = () => {
    if (!artwork || !inStock) return;
    const item = {
      id: artwork._id,
      title: displayTitle,
      price: artwork.price,
      imageUrl: images[0] || null,
      quantity,
      frameOption,
      totalPrice: artwork.price * quantity,
    };
    if (!isAuthenticated) {
      setPendingCartItem(item);
      setShowLoginModal(true);
      return;
    }
    persistCartItem(item);
  };

  const completePendingCart = (success) => {
    setShowLoginModal(false);
    setShowRegisterModal(false);
    if (success && pendingCartItem) {
      persistCartItem(pendingCartItem);
      setPendingCartItem(null);
    }
  };

  if (loading) {
    return <div className="tafsir-page"><div className="page-shell grid min-h-[55vh] place-items-center"><div className="skeleton-line h-[70vh] w-full max-w-5xl" /></div></div>;
  }

  if (!artwork) {
    return (
      <div className="tafsir-page">
        <div className="page-shell quiet-surface mx-auto max-w-2xl px-6 py-20 text-center">
          <h1 className="font-display text-4xl font-semibold">{t('detail.unavailable')}</h1>
          <Link to={isGallery ? '/gallery' : '/store'} className="btn-primary mt-8">{isGallery ? t('detail.backGallery') : t('detail.backStore')}</Link>
        </div>
      </div>
    );
  }

  const backPath = isGallery ? '/gallery' : '/store';
  const relatedPath = (relatedId) => isGallery ? `/gallery/artwork/${relatedId}` : `/store/artwork/${relatedId}`;

  return (
    <div className="tafsir-page">
      <div className="page-shell">
        <Link to={backPath} className="mb-8 inline-flex items-center gap-2 text-sm font-medium text-text-secondary hover:text-primary">
          {isRTL ? <ArrowRight size={17} /> : <ArrowLeft size={17} />}
          {isGallery ? t('detail.backGallery') : t('detail.backStore')}
        </Link>

        {!isGallery && (
          <header className="mb-9 border-b border-border pb-7 text-center">
            <p className="editorial-eyebrow mb-3">{t('detail.storeEyebrow')}</p>
            <h1 className="mx-auto max-w-5xl font-display text-[clamp(2.8rem,6vw,5.7rem)] font-semibold leading-[.95] tracking-[-.04em] text-text-primary">{displayTitle}</h1>
            {arabicTitle && language !== 'ar' && <p className="mt-3 font-arabic text-2xl text-text-secondary" dir="rtl" lang="ar">{arabicTitle}</p>}
          </header>
        )}

        <div className={`grid ${isGallery ? 'gap-0' : 'gap-10 xl:grid-cols-[minmax(0,1.15fr)_minmax(380px,.75fr)]'}`}>
          <section aria-label={t('detail.otherViews')}>
            <div className="grid min-h-[58vh] place-items-center bg-surface px-5 py-8 sm:px-10">
              {images.length > 0 ? (
                <img src={images[selectedImage]} alt={`${displayTitle} — ${selectedImage + 1}`} className="max-h-[72vh] w-auto max-w-full object-contain shadow-[0_20px_55px_var(--shadow-lg)] transition duration-500" />
              ) : (
                <p className="text-text-muted">No image available</p>
              )}
            </div>

            {images.length > 1 && (
              <div className="mt-5">
                <p className="editorial-eyebrow mb-3 text-center">{t('detail.otherViews')}</p>
                <div className="flex flex-wrap justify-center gap-3">
                  {images.map((image, index) => (
                    <button key={image} type="button" onClick={() => setSelectedImage(index)} aria-pressed={selectedImage === index} className={`h-20 w-20 overflow-hidden border bg-surface p-1 transition ${selectedImage === index ? 'border-primary' : 'border-border opacity-70 hover:opacity-100'}`}>
                      <img src={image} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </section>

          {isGallery && (
            <header className="mx-auto mt-12 w-full max-w-5xl border-b border-border pb-9 text-center sm:mt-16">
              <p className="editorial-eyebrow mb-3">{artwork.category}</p>
              <h1 className="font-display text-[clamp(2.8rem,6vw,5.7rem)] font-semibold leading-[.95] tracking-[-.04em] text-text-primary">{displayTitle}</h1>
              {arabicTitle && language !== 'ar' && <p className="mt-3 font-arabic text-2xl text-text-secondary" dir="rtl" lang="ar">{arabicTitle}</p>}
            </header>
          )}

          <aside className={isGallery ? 'mx-auto w-full max-w-3xl py-10 text-center sm:py-12' : 'self-start border-t border-text-primary pt-6 xl:sticky xl:top-[calc(var(--header-height)+2rem)]'}>
            {!isGallery && <p className="editorial-eyebrow">{artwork.category}</p>}
            <h2 className="mt-5 font-display text-3xl font-semibold">{t('detail.description')}</h2>
            <p className="mt-3 leading-8 text-text-secondary">{displayDescription}</p>
            {arabicDescription && language !== 'ar' && (
              <p className="mt-6 border-r-2 border-primary pr-4 text-right font-arabic text-xl leading-9 text-text-secondary" dir="rtl" lang="ar">{arabicDescription}</p>
            )}

            {!isGallery && (
              <div className="mt-8 space-y-7 border-t border-border pt-7">
                <div>
                  <span className="editorial-eyebrow">{t('detail.price')}</span>
                  <div className="mt-1 font-display text-4xl font-semibold text-text-primary">{formatCurrency(artwork.price)}</div>
                  <p className={`mt-1 text-sm ${inStock ? 'text-text-secondary' : 'text-primary'}`}>{inStock ? t('detail.inStock', { count: availableQuantity }) : t('detail.outOfStock')}</p>
                </div>

                {artwork.dimensions && (
                  <div className="grid grid-cols-[110px_1fr] gap-4 border-t border-border pt-5 text-sm">
                    <span className="font-semibold text-text-primary">{t('detail.dimensions')}</span>
                    <span className="text-text-secondary">{artwork.dimensions.width} × {artwork.dimensions.height}{artwork.dimensions.depth ? ` × ${artwork.dimensions.depth}` : ''} {artwork.dimensions.unit}</span>
                  </div>
                )}

                <label className="block">
                  <span className="mb-2 block text-sm font-semibold">{t('detail.frame')}</span>
                  <select value={frameOption} onChange={(event) => setFrameOption(event.target.value)} className="field">
                    {frames.map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                </label>

                <div>
                  <span className="mb-2 block text-sm font-semibold">{t('detail.quantity')}</span>
                  <div className="inline-flex h-12 items-center border border-border bg-surface">
                    <button type="button" onClick={() => setQuantity((value) => Math.max(1, value - 1))} className="grid h-full w-12 place-items-center" aria-label="Decrease quantity"><Minus size={16} /></button>
                    <span className="grid h-full min-w-12 place-items-center border-x border-border text-sm font-semibold">{quantity}</span>
                    <button type="button" onClick={() => setQuantity((value) => Math.min(Math.max(1, availableQuantity), value + 1))} className="grid h-full w-12 place-items-center" aria-label="Increase quantity"><Plus size={16} /></button>
                  </div>
                </div>

                <button type="button" onClick={handleAddToCart} disabled={!inStock} className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-45">
                  {inStock && <Check size={18} />}{inStock ? t('detail.add') : t('detail.outOfStock')}
                </button>
              </div>
            )}

          </aside>
        </div>

        {relatedArtworks.length > 0 && (
          <section className="mt-24 border-t border-border pt-9">
            <div className="mb-8 flex items-end justify-between gap-4">
              <div>
                <p className="editorial-eyebrow mb-2">Tafsir Art</p>
                <h2 className="font-display text-4xl font-semibold">{isGallery ? t('detail.relatedGallery') : t('detail.relatedStore')}</h2>
              </div>
              <Link to={backPath} className="text-sm font-semibold text-primary">{isGallery ? t('common.gallery') : t('common.store')}</Link>
            </div>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {relatedArtworks.map((item) => (
                <Link key={item._id} to={relatedPath(item._id)} className="group">
                  <div className="h-64 overflow-hidden bg-surface"><img src={item.images?.[0]} alt={getText(item.title, language)} className="h-full w-full object-cover transition duration-700 group-hover:scale-[1.03]" /></div>
                  <h3 className="mt-3 font-display text-xl font-semibold">{getText(item.title, language)}</h3>
                  {!isGallery && <p className="mt-1 text-sm font-semibold text-primary">{formatCurrency(item.price)}</p>}
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      <LoginModal isOpen={showLoginModal} onClose={completePendingCart} onSwitchToRegister={() => { setShowLoginModal(false); setShowRegisterModal(true); }} returnPath={`/store/artwork/${id}`} />
      <RegisterModal isOpen={showRegisterModal} onClose={completePendingCart} onSwitchToLogin={() => { setShowRegisterModal(false); setShowLoginModal(true); }} />
    </div>
  );
};

export default ProductDetail;
